import { useId, useState } from "react";
import { reservationsApi } from "@/api/client";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import { Input, Label } from "@/pages/AddService/styles";
import { newResetOpId } from "@/pages/PipelineDetail/positionReset";
import { parseLsn } from "@/pages/slotIntent";
import type { DropObservation } from "@/types/api";
import {
  dropMessage,
  dropRequest,
  type ReservationRow,
} from "./reservationRows";

interface ReleaseSlotModalProps {
  row: ReservationRow | null;
  onClose: () => void;
}

/**
 * Releases a retained slot, or reconciles one whose earlier drop's outcome is
 * unknown. A drop destroys the position the deleted pipeline left, so it says
 * so before asking, and it is never sent twice: one confirmation keeps one
 * operation id across retries, and a reconciliation is a new operation made
 * from what the operator sees on the server.
 */
export function ReleaseSlotModal({ row, onClose }: ReleaseSlotModalProps) {
  // Keyed by the slot, so each one opens with its own operation and fields.
  return row && <ReleaseDialog key={row.id} row={row} onClose={onClose} />;
}

function ReleaseDialog({
  row,
  onClose,
}: {
  row: ReservationRow;
  onClose: () => void;
}) {
  const drop = useSlotDrop(row, onClose);
  const wording = WORDING[row.needsReconciliation ? "reconcile" : "release"];
  const action =
    drop.result === null
      ? { label: wording.confirm, type: "error" as const }
      : { label: "Close", type: "primary" as const };

  return (
    <ConfirmDeleteModal
      isOpen
      title={wording.title}
      message={
        drop.result ?? (
          <>
            <p>{wording.ask(row)}</p>
            {row.needsReconciliation && (
              <Observation
                absent={drop.absent}
                onAbsent={drop.setAbsent}
                lsn={drop.lsn}
                onLsn={drop.setLsn}
              />
            )}
          </>
        )
      }
      confirmLabel={action.label}
      confirmType={action.type}
      hideCancel={drop.result !== null}
      loading={drop.sending}
      loadingLabel="Dropping…"
      error={drop.error}
      onConfirm={drop.confirm}
      onCancel={onClose}
    />
  );
}

// One drop: its operation id, what the operator reports, and its outcome.
function useSlotDrop(row: ReservationRow, onClose: () => void) {
  const [opId] = useState(newResetOpId);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [absent, setAbsent] = useState(false);
  const [lsn, setLsn] = useState(() => knownPosition(row));

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const outcome = await reservationsApi.drop(
        dropRequest(row, opId, observed(row, absent, lsn)),
      );
      setResult(dropMessage(outcome));
    } catch (err) {
      // Kept open with the same operation id: sending it again is the same
      // drop, which the engine answers from its record.
      setError(err instanceof Error ? err.message : "The drop failed");
    } finally {
      setSending(false);
    }
  };

  // Once the drop has an outcome, the button only closes the dialog.
  const confirm = () => (result === null ? send() : onClose());

  return { sending, result, error, absent, setAbsent, lsn, setLsn, confirm };
}

/** The slot's confirmed position as read from the server, to start from. */
function knownPosition(row: ReservationRow): string {
  return row.confirmedFlushLsn === undefined
    ? ""
    : String(row.confirmedFlushLsn);
}

/** What the dialog says for each of its two jobs. */
const WORDING = {
  release: {
    title: "Release slot",
    confirm: "Drop slot",
    ask: (row: ReservationRow) =>
      `Drop slot "${row.slot}", retained from deleted pipeline "${row.owner}"? Its position is lost for good, and the WAL it keeps is released.`,
  },
  reconcile: {
    title: "Reconcile slot drop",
    confirm: "Reconcile",
    ask: (row: ReservationRow) =>
      `An earlier drop of slot "${row.slot}" may or may not have reached the server. Say what you see on it; a slot still there is dropped again, once.`,
  },
};

/** What the operator reports about a slot whose drop is uncertain; nothing
 *  for a plain release. */
function observed(
  row: ReservationRow,
  absent: boolean,
  lsn: string,
): DropObservation | undefined {
  if (!row.needsReconciliation) return undefined;
  if (absent) return "absent";
  return { slot: { confirmed_flush_lsn: parseLsn(lsn) } };
}

/** The operator's account of the slot: gone, or at a position. */
function Observation({
  absent,
  onAbsent,
  lsn,
  onLsn,
}: {
  absent: boolean;
  onAbsent: (absent: boolean) => void;
  lsn: string;
  onLsn: (lsn: string) => void;
}) {
  const lsnId = useId();
  return (
    <>
      <label>
        <input
          type="checkbox"
          checked={absent}
          onChange={(e) => onAbsent(e.target.checked)}
        />{" "}
        The slot is gone from the server
      </label>
      {!absent && (
        <>
          <Label htmlFor={lsnId}>Its confirmed position now</Label>
          <Input
            id={lsnId}
            type="text"
            value={lsn}
            onChange={(e) => onLsn(e.target.value)}
            placeholder="0/16B3748"
          />
        </>
      )}
    </>
  );
}
